// Merkan · Anleitungen laden (RPC anleitungen_fuer), Cache je Mandant, tolerante Suche.
// Die Liste ist klein und ändert sich selten: einmal je Mandant und Seitenaufruf laden, danach aus dem Speicher.
import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import { t } from '../lib/sprache';

export interface TourSchritt {
  /** CSS-Selektor des Ziels; ohne Ziel erscheint der Schritt mittig */
  ziel?: string;
  titel: string;
  text: string;
  /** Fester Pfad (Engine wechselt dorthin) oder Muster mit * (nur Prüfung) */
  route?: string;
  aktion?: 'klick';
  optional?: boolean;
  /** Abweichung bis 860 px Breite */
  mobil?: { ziel?: string; text?: string; titel?: string };
}

export interface AnleitungBild { name: string; titel: string }

export interface Anleitung {
  id: string;
  bereich: string;
  titel: string;
  kurz: string;
  text: string;
  schlagworte: string[];
  route: string | null;
  schritte: TourSchritt[];
  bilder: AnleitungBild[];
  sortierung: number;
  stand: string;
  freigeschaltet: boolean;
}

interface Eintrag { liste: Anleitung[] | null; fehler: string | null }
const speicher = new Map<string, Eintrag>();
const laufend = new Map<string, Promise<Anleitung[]>>();
const hoerer = new Set<() => void>();
/** Einträge werden ersetzt, nie verändert, damit useSyncExternalStore die Änderung erkennt */
function eintragSetzen(mandantId: string, e: Eintrag) {
  speicher.set(mandantId, e);
  hoerer.forEach((h) => h());
}

/** Lädt die Anleitungen des Mandanten (einmal, danach aus dem Cache). neu = true lädt erneut. */
export function anleitungenLaden(mandantId: string, neu = false): Promise<Anleitung[]> {
  const vorhanden = speicher.get(mandantId);
  if (!neu && vorhanden?.liste) return Promise.resolve(vorhanden.liste);
  const l = laufend.get(mandantId);
  if (l) return l;
  const p = (async () => {
    try {
      const { data, error } = await supabase.rpc('anleitungen_fuer', { p_mandant: mandantId });
      if (error) throw error;
      const liste = ((data ?? []) as Anleitung[]).map((a) => ({
        ...a,
        schlagworte: a.schlagworte ?? [],
        schritte: Array.isArray(a.schritte) ? a.schritte : [],
        bilder: Array.isArray(a.bilder) ? a.bilder : [],
        freigeschaltet: a.freigeschaltet !== false,
      }));
      eintragSetzen(mandantId, { liste, fehler: null });
      return liste;
    } catch {
      const alt = speicher.get(mandantId)?.liste ?? [];
      eintragSetzen(mandantId, { liste: alt, fehler: t('Die Anleitungen konnten gerade nicht geladen werden.') });
      return alt;
    } finally {
      laufend.delete(mandantId);
    }
  })();
  laufend.set(mandantId, p);
  return p;
}

/** Bereits geladene Liste ohne zu warten (null = noch nicht geladen) */
export const anleitungenSofort = (mandantId: string | null | undefined): Anleitung[] | null =>
  (mandantId ? speicher.get(mandantId)?.liste ?? null : null);

export const anleitungFinden = (mandantId: string | null | undefined, id: string): Anleitung | null =>
  anleitungenSofort(mandantId)?.find((a) => a.id === id) ?? null;

/** Liste für Komponenten; lädt beim ersten Aufruf je Mandant */
export function useAnleitungen(mandantId: string | null | undefined): { liste: Anleitung[] | null; fehler: string | null } {
  const stand = useSyncExternalStore(
    (h) => { hoerer.add(h); return () => { hoerer.delete(h); }; },
    () => (mandantId ? speicher.get(mandantId) ?? null : null),
  );
  useEffect(() => { if (mandantId) void anleitungenLaden(mandantId); }, [mandantId]);
  return { liste: stand?.liste ?? null, fehler: stand?.fehler ?? null };
}

/** Kleinschreibung, Umlaute vereinfacht, für tolerante Suche (wie im Wiki) */
export const norm = (s: string) => s.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss');

/** Treffer über Titel, Kurztext, Schlagworte und Text; mehrere Wörter müssen alle vorkommen */
export function anleitungPasst(a: Anleitung, suche: string): boolean {
  const woerter = norm(suche.trim()).split(/\s+/).filter(Boolean);
  if (!woerter.length) return true;
  const heu = norm([a.titel, a.kurz, a.bereich, a.schlagworte.join(' '), a.text].join(' '));
  return woerter.every((w) => heu.includes(w));
}

/** Nach Bereich gruppieren, Reihenfolge wie geliefert (RPC sortiert nach sortierung) */
export function nachBereich(liste: Anleitung[]): { bereich: string; anleitungen: Anleitung[] }[] {
  const gruppen: { bereich: string; anleitungen: Anleitung[] }[] = [];
  const index = new Map<string, number>();
  for (const a of liste) {
    let i = index.get(a.bereich);
    if (i == null) { i = gruppen.length; index.set(a.bereich, i); gruppen.push({ bereich: a.bereich, anleitungen: [] }); }
    gruppen[i].anleitungen.push(a);
  }
  return gruppen;
}

/** Pfad eines Bildes für ein Gerät */
export const bildPfad = (id: string, name: string, geraet: 'pc' | 'tablet' | 'handy') => `/anleitungen/${id}/${name}-${geraet}.webp`;
